import * as React from "react";

import { Badge, COLOR, PLACEMENT, SHAPE } from "baseui/badge";

import { Icon } from "kanopi-components/icons/Icon";
import { LeftNav } from "kanopi-components/LeftNav/LeftNav";
import { NavItemProps } from "baseui/side-navigation";
import { useEffect } from "react";
import { useLocation } from "@reach/router";
import { useNavigate } from "@reach/router";
import { useThemeContext } from "kw/themeContext";
import { buildUpdateClaimsAllowed, buildUpdateReferredPendingCount } from "./callables";

type TPortalSidebar = {
  userName: string;
  isNotOverlay?: boolean;
  allowAdminPortalAccess?: boolean;
  mainItems?: NavItemProps[];
  userItems?: NavItemProps[];
  onOpenClose?: (open: boolean) => void;
};

export const PortalSidebar = (props: TPortalSidebar) => {
  const {
    userName,
    allowAdminPortalAccess = false,
    mainItems = [],
    userItems = [],
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const { context } = useThemeContext();
  const split = location.pathname.split("/");
  let activeItemId = `/${split[1]}`;
  if (split[1] == "admin" && split[2]) {
    activeItemId += `/${split[2]}`;
  }

  const [referredPendingCount, setReferredPendingCount] = React.useState(0);
  const [claimsAllowed, setClaimsAllowed] = React.useState(false);

  const updateReferredPendingAndClaimsAllowedCount = buildUpdateReferredPendingCount(
    {
      referredPendingCount,
      setReferredPendingCount,
      claimsAllowed,
      setClaimsAllowed,
    },
    [setReferredPendingCount, setClaimsAllowed]
  );

  const updateClaimsAllowed = buildUpdateClaimsAllowed(
    {
      claimsAllowed,
      setClaimsAllowed,
    },
    [setClaimsAllowed]
  );

  useEffect(() => {
    const repeatingFunction = allowAdminPortalAccess
      ? updateReferredPendingAndClaimsAllowedCount
      : updateClaimsAllowed;
    repeatingFunction();

    const delay = 5000;
    let timerId = setTimeout(function repeater() {
      repeatingFunction();
      timerId = setTimeout(repeater, delay);
    }, delay);

    return () => clearTimeout(timerId);
  }, []);

  const items = React.useMemo(() => {
    let navItems;
    if (allowAdminPortalAccess) {
      navItems = [
        {
          itemId: "/admin",
          title: "Dashboard",
          url: "/admin",
          icon: <Icon icon="home-02" />,
        },
        {
          itemId: "/admin/customers",
          title: "Customers",
          url: "/admin/customers",
          icon: <Icon icon="user-circle" />,
        },
        {
          itemId: "/admin/quotes",
          title: "Quotes",
          url: "/admin/quotes",
          icon: <Icon icon="currency-dollar" />,
          hintOnCollapse: !!referredPendingCount,
          endEnhancer: referredPendingCount && (
            <Badge
              content={referredPendingCount}
              placement={PLACEMENT.topRight}
              shape={SHAPE.pill}
              color={COLOR.negative}
            />
          ),
        },
        {
          itemId: "/admin/policies",
          title: "Policies",
          url: "/admin/policies",
          icon: <Icon icon="file-02" />,
        },
      ];
      if (claimsAllowed) {
        navItems.push({
          itemId: "/admin/claims",
          title: "Claims",
          url: "/admin/claims",
          icon: <Icon icon="edit" />,
        });
      }
      navItems = navItems.concat([
        {
          itemId: "/admin/reports",
          title: "Reports",
          url: "/admin/reports",
          icon: <Icon icon="reports-w" />,
        },
        {
          itemId: "/admin/contact-us",
          title: "Contact us",
          url: "/admin/contact-us",
          icon: <Icon icon="mail" />,
        },
      ]);
    } else {
      navItems = [
        {
          itemId: "/policies",
          title: "Policies",
          url: "/policies",
          icon: <Icon icon="file-02" />,
        },
      ];
      if (claimsAllowed) {
        navItems.push({
          itemId: "/claims",
          title: "Claims",
          url: "/claims",
          icon: <Icon icon="edit" />,
        });
      }
      navItems.push({
        itemId: "/contact-us",
        title: "Contact us",
        url: "/contact-us",
        icon: <Icon icon="mail" />,
      });
      navItems.push({
        itemId: "/account",
        title: "Account",
        url: "/account",
        icon: <Icon icon="user-circle" />,
      });
    }
    return navItems;
  }, [allowAdminPortalAccess, referredPendingCount, claimsAllowed]);

  return (
    <LeftNav
      title={userName}
      navigate={navigate}
      activeItemId={activeItemId}
      navigationItems={items}
      mainItems={mainItems}
      userItems={userItems}
      onOpenClose={props?.onOpenClose}
      logoSrc={context?.logo?.src}
      logoAltText={context?.logo?.altText}
    />
  );
};
